import NavigationController from "./controller/NavigationController";
import DataHelper from "./data/DataHelper";
import IssueAudio from "./IssueAudio";
import InterfaceController from "./InterfaceController";
import Message from "./Message";
import Utils, { focusableSelector, getSafeRedirectUrl } from "./utils/Utils";
import NotificationBar from "./utils/NotificationBar";
import OfflineViewer from "./OfflineViewer";
import CookieConsent from "./CookieConsent";
import GatedContent from "./GatedContent";
import Analytics from "./utils/Analytics";
import Pirsch from "./utils/Pirsch";

const SPREAD_META_TITLE_KEY = 8;
const ISSUE_META_TITLE_KEY = 26;

// Project type constants
const PROJECT_TYPES = {
    SINGLEPAGE: 1,
    STORY: 2,
    VERTICALSWIPE: 3,
};

// Modes that require special interface handling (story-like modes)
const SPECIAL_MODES = [PROJECT_TYPES.STORY, PROJECT_TYPES.VERTICALSWIPE];

class FrontendController {
    constructor() {
        this._LANG = "nl_NL";
        this._spread_id;
        this._issue_id;
        this.issues = new Map();
        this.spreadOrder = [];
        this._operator = {};
        this.fastload = false;

        //expose jquery for external usage
        self.$ = self.$ || $;
        self.jQuery = $;

        this.init();

        this.mobileView = window.innerWidth < 992;

        this._spreads = {};

        this.SPREADSTATUSLIST = {
            INIT: 0,
            WAITINGTOCREATE: 10,
            CREATED: 20,
            STOPPED: 21,
            PAUSED: 25,
            READYTOSTART: 30,
            STARTED: 40,
        };
    }

    init() {
        this.setFrontendFilesMode();
        this.IssueAudio = new IssueAudio();
        this.dataHelper = new DataHelper();
        this.interface = new InterfaceController(this);
        this.analytics = new Analytics();
        this.pirsch = window.maglr_pirsch = new Pirsch(
            window.maglrAnalyticsSettings ?? window.interface_settings?.settings?.analytics,
        );
        window.addEventListener("message", (event) => this.messageController(event));

        this.mainStage = document.getElementById("content-slider");

        document.body.addEventListener("focusin", (e) => {
            try {
                const prevState = this.focusMainStage;
                this.focusMainStage = !!e.target.closest("#content-slider");

                let inPreload = false;
                if (this.focusMainStage && e.target.closest(".preloadedSpread")) {
                    inPreload = true;
                }

                if (this.interface.storyMode && e.target.closest("#story-background")) {
                    inPreload = true;
                }

                if (inPreload) {
                    if (prevState === true) {
                        // focus stond in mainStage en gaat naar preloaded spread => leg focus op interface
                        let firstFocusable = null;
                        let doFocus = 5;
                        while (doFocus > 0) {
                            //loop through first 5 items because there can be hidden items that can have focus by the selector but actually can not
                            doFocus--;
                            firstFocusable = document.querySelector(".interface")?.querySelector?.(focusableSelector);
                            if (firstFocusable) {
                                firstFocusable && firstFocusable.focus();
                                if (document.activeElement === firstFocusable) {
                                    doFocus = 0;
                                } else {
                                    firstFocusable.setAttribute("disabled", "true");
                                }
                            }
                        }
                    } else {
                        // focus stond in interface en gaat naar preloaded spread => leg focus in active spread
                        const activeSpread = this.mainStage.querySelector("#maglr-article-" + this._spread_id);
                        const focusable = activeSpread?.querySelector?.(focusableSelector);
                        if (focusable) {
                            focusable.focus();
                        } else {
                            activeSpread?.querySelector("[data-spread_id]").focus();
                        }
                    }
                    return;
                }
            } catch (error) {
                console.error("MAGLR ERROR: [focusin]", error);
            }
        });

        this.backStage = document.getElementById("content-loader");

        window.addEventListener("resize", () => {
            let mobile = window.innerWidth < 992;

            if (mobile !== this.mobileView) {
                this.mobileView = mobile;
                this.advancedController &&
                    this.advancedController.updateInterfaceOffset(this.getInterfaceOffsetSettings());
            }
        });

        try {
            if (window?.navigator?.platform?.toLowerCase().indexOf("mac") > -1) {
                document.body.classList.add("os-scrollbars");
                document.querySelector("html").classList.add("os-scrollbars");
            } else if (window?.navigator?.platform?.toLowerCase().indexOf("win") > -1) {
                // Fixes bug on windows + edge where overscroll-behavior: none causes to not scroll at all
                document.body.classList.add("overscrollAuto");
            }
        } catch (error) {
            console.warn("navigator.platform error", error);
        }

        this.bindKeyDownEvents();

        window.addEventListener("MaglrHistoryPush", (event) => {
            if (this.getCurrentPublicationType() === 4) {
                window.scrollToSpread && window.scrollToSpread(event.detail?.spreadId);
            } else {
                event.detail &&
                    this.gotoIssueSpread(parseInt(event.detail.issueId, 10), parseInt(event.detail.spreadId, 10));
            }
        });
        window.dispatchEvent(Utils.createEvent("FrontendReady"));

        if (window.parent !== window) {
            this.embedMode = true;
            this.embedIdentifier = window.location.pathname;
            window.parent.postMessage({ message: "HELLO" }, "*");
        }

        if (typeof sendOVMessage == "function") {
            this.offline = new OfflineViewer();
        }

        //check if a cookie wall is enabled
        if (window.interface_settings?.settings?.cookieConsent) {
            this.CookieConsent = new CookieConsent(window.interface_settings.settings.cookieConsent);

            this.CookieConsent.on("ConsentChanged", (event) => {
                if (event.consent) {
                    this.advancedController && this.advancedController.setCookieConsented(event.consent);
                    this.iframeController && this.iframeController.setCookieConsented(event.consent);
                }
            });
        }

        if (window.parent !== window) {
            //ADD CLASS overscrollAuto to body and html tags.
            document.body.classList.add("overscrollAuto");
        }
    }

    getDataHelper() {
        return this.dataHelper;
    }

    setFrontendFilesMode() {
        window.downloaded = 0; //live aka 'the old way'
        if (Utils.isEmpty(window.data_domain)) {
            window.downloaded = 1; //local aka inline
            window.data_domain = "";
        }
    }

    messageController(event) {
        try {
            this.messageReceivedFrontend(event);
        } catch (error) {
            console.error("MAGLR ERROR: [messageController]", error);
        }
    }

    getSpreadController(spread_id, issueId = false) {
        const issue_id = issueId || this._issue_id;
        if (this._operator.checkInlinePossible(spread_id, issue_id)) {
            if (this.advancedController === undefined) {
                this.advancedController = new (require("./controller/AdvancedController").default)(
                    window.latestAdvancedVersions,
                );
            }
            return this.advancedController;
        } else {
            if (this.iframeController === undefined) {
                // import IframeController from './controller/IframeController';
                this.iframeController = new (require("./controller/IframeController").default)();
            }
            return this.iframeController;
        }
    }

    /**
     * Start the application based on
     * @param issue_id
     */
    start() {
        this.analytics.bindUserEventsButtonClick();
        // Utils.bindUserEventsButtonClick();

        if (Utils.isDownloaded()) {
            const gets = NavigationController.getGetParams();
            Object.hasOwn(gets, "gated") && GatedContent.setCookie(gets.gated);

            // import OfflineController from './model/OfflineController';
            // this._operator = new OfflineController( this );
            this._operator = new (require("./model/OfflineController").default)(this);
            this._operator.init();
        } else {
            // import OnlineController from './model/OnlineController';
            // this._operator = new OnlineController( this );
            this._operator = new (require("./model/OnlineController").default)(this);
            this._operator.init();
        }

        //set a timeout that pushes an event after x seconds when no other event has been pushed yet
        this.bounceRateTimer = setTimeout(() => {
            try {
                this.analytics.sendEventUpdate(
                    this.analytics.createUserEvent(["User-still-there?", `No-interaction`, "15-seconds"]),
                );
            } catch {
                // continue regardless of error
            }
        }, 15000);
    }

    /**
     * return the current initiated issue_id, can be undefined
     * @return {*}
     */
    getCurrentIssueId() {
        return this._issue_id;
    }

    /**
     * return the current initiated spread_id, can be undefined
     * @return {*}
     */
    getCurrentSpreadId() {
        return this._spread_id;
    }

    createSpreadOrder(spreads = undefined) {
        this.spreadOrder = this.dataHelper.getSpreadIdListOrder(this._issue_id, spreads);
    }

    /**
     * Fix rotation if fastload because we shuffle the spreads and transition only knows next and prev div
     * so we need to move the div
     * Added skip too.
     */
    fixRotateSpread() {
        try {
            if (this.dataHelper._spread_skip) {
                for (var id in this.dataHelper._spread_skip) {
                    var spreadId = +this.dataHelper._spread_skip[id];
                    if ($("#" + spreadId).length > 0) {
                        $("#" + spreadId)
                            .parent()
                            .remove();
                    }
                }
            }

            if (this.dataHelper._moveDomRotate) {
                for (var spread_id in this.dataHelper._moveDomRotate) {
                    var i = this.spreadOrder.indexOf(+spread_id);
                    if (i > -1) {
                        var container = $("article.maglr-article");
                        container.eq(i).append(this.dataHelper._moveDomRotate[spread_id]);
                        if (
                            $(".current-spread").find("[data-spread-id]").data("spread-id") !== this._spread_id &&
                            this._spread_id == spread_id
                        ) {
                            container.removeClass("current-spread");
                            container.eq(i).addClass("current-spread");
                        }
                    }
                    delete this.dataHelper._moveDomRotate[spread_id];
                }
            }
        } catch (error) {
            console.error("MAGLR ERROR: [fixRotateSpread]", error);
        }
    }

    /**
     * Unload html and spread class after 2 seconds of inactivity
     */
    unloadSpreads() {
        clearTimeout(this.destoryTimer);
        let preload = 1;
        // preload++; // ++ so we dont have to do + 1 twice in the loop
        this.destoryTimer = setTimeout(() => {
            let dontUnload = [];
            if (this.getCurrentIssueId()) {
                if (this.dataHelper.hasMainSpreadNavigation(this._issue_id)) {
                    let mainSpreadId = this._spread_id;

                    if (!this.dataHelper.isMainSpread(this.getCurrentIssueId(), this._spread_id)) {
                        //current spread is not in topLevel, push current and find main spread
                        dontUnload.push(this._spread_id);
                        mainSpreadId = this.dataHelper.findMainSpreadIdOfSubSpread(
                            this.getCurrentIssueId(),
                            this._spread_id,
                        );
                    }
                    let level = this.dataHelper.getMainSpreadsTopLevel(this.getCurrentIssueId());
                    let index = this.dataHelper.getMainSpreadIndexBySpreadId(this.getCurrentIssueId(), mainSpreadId);

                    //slice the portion of the toplevel in the preload range and add it to the dontUnload array.
                    dontUnload.push(
                        ...level
                            .slice(Math.max(index - preload, 0), index + 1 + preload)
                            .map((s) => parseInt(s.id, 10)),
                    );

                    //dont unload the whole sub-level when the active spread is a subspread.
                    if (mainSpreadId !== this._spread_id && level[index]?.children.length > 0) {
                        dontUnload.push(...level[index].children.map((s) => parseInt(s.id, 10)));
                    }

                    //never unload the spread we are currently active on!
                    dontUnload.push(this._spread_id);
                } else {
                    let index = this.spreadOrder.indexOf(this._spread_id);
                    //slice the portion that's in the preloaded range and add it to the dontUnload array.
                    dontUnload.push(...this.spreadOrder.slice(Math.max(index - preload, 0), index + 1 + preload));
                }
            }

            this.spreadOrder.forEach((spread_id) => {
                if (dontUnload.indexOf(spread_id) === -1) {
                    try {
                        if (this.getSpreadController(spread_id).destroySpread(spread_id)) {
                            delete this.interface._scrollingList[spread_id];
                            if (this._spreads[spread_id]?.settings && !this._spreads[spread_id].settings.html) {
                                // preloaded pro spread does have settings but not the html, delete so it will be fetched again when needed
                                delete this._spreads[spread_id];
                            }
                            $(`#spread_${spread_id}`)
                                .html("")
                                .removeClass("shown stopped")
                                .closest(`.spread-container`)
                                .css("transform", ``);
                        }
                    } catch (e) {
                        console.warn(e);
                    }
                    this.moveSpreadToBackStage(spread_id);
                }
            });
        }, 2000);
    }

    renderHomepage(category = undefined) {
        this._homepage = true;
        this.interface.renderHomepage(category);
    }

    getSpreadAssetsVersion(spread_id, issueId = false) {
        const issue_id = issueId || this._issue_id;
        if (Utils.isEmpty(issue_id) || issue_id == 0) {
            return 0;
        }
        const spread = this.dataHelper.getSpreadById(issue_id, spread_id);
        if (!Utils.isEmpty(spread)) {
            if (window.previewMode) {
                return spread.assets_version_preview || spread.assets_version_js;
            } else {
                return spread.assets_version_js;
            }
        }
        return 0;
    }

    getSpreadType(spread_id, issueId = false) {
        const issue_id = issueId || this._issue_id;
        if (Utils.isEmpty(issue_id) || issue_id == 0) {
            return false;
        }
        const spread = this.dataHelper.getSpreadById(issue_id, spread_id);
        if (!Utils.isEmpty(spread)) {
            return spread.spread_type;
        }
        return false;
    }

    /**
     * Do a navigation based on the given index we receive
     * Example: to navigate to first spread of the issue
     * @param index
     */
    gotoIndex(index) {
        this.gotoSpread(this.spreadOrder[index]);
    }
    endNavigation() {
        this.gotoSpread(this._to_spread_id);
        this._to_spread_id = undefined;
    }

    resetIssueSpreads() {
        if (this.advancedController) {
            this.advancedController.destroyAll();
        }
    }

    /**
     * Fire the stop on given spread_id
     * @param spread_id
     */
    executeStopSpread(spread_id) {
        if (Utils.isEmpty(spread_id)) {
            spread_id = this._spread_id;
        }

        if (spread_id) {
            let control = this.getSpreadController(spread_id).stopSpread(spread_id);
            this.setSpreadStatus(spread_id, this.SPREADSTATUSLIST.STOPPED);

            if (this.getSpreadType(spread_id) === 2 && this.getSpreadAssetsVersion(spread_id) < 10) {
                //For old pro pages (version < 10) that run in an iframe destroy the spread so it can restart
                control && control.destroySpread(spread_id);
            }
        }
    }

    /**
     * Do a navigation based on the given spread_id
     * @param spread_id
     * @return {boolean}
     */
    gotoSpread(spread_id) {
        if (this.gatedContent?.hasActiveGate?.()) {
            return false;
        }

        if (this._spread_id != spread_id) {
            // this.unloadSpreads();

            if (this._operator.hasUrlListener()) {
                this.interface.handleStopSpread(this._spread_id);
            } else {
                this.executeStopSpread();
            }

            if (this.spreadOrder.indexOf(spread_id) > -1) {
                // this._operator.fetchSpreadSettings(spread_id); // no need to fetch here, it will be done on history push.

                this.setCurrentSpreadId(spread_id);
                if (!this._operator.hasUrlListener()) {
                    if (this.interface.storyMode) {
                        this.interface.doStoryModeNavigation(spread_id);
                    } else {
                        this.interface.moveSliderToSpread(spread_id);
                    }
                }

                this.interface.controlsCheck(this._spread_id, this.spreadOrder);

                this.interface?.sidemenu.closeMenu();
                //offline must have history back function..
                // if (this._operator.hasUrlListener()) {
                let spread = this.dataHelper.getSpreadById(this._issue_id, this._spread_id),
                    url =
                        spread.url ||
                        `${this._LANG}/${this._issue_id}/${spread.spread_id}/${Utils.urlEncodeString(spread.spread_title)}.html`;
                NavigationController.pushState({}, this.dataHelper.issues[this._issue_id].issue_title, `/${url}`);
                // }

                return true;
            }
        } else {
            return false;
        }
    }

    gotoIssueSpread(issueId, spreadId) {
        if (issueId === this._issue_id) {
            this.gotoSpread(spreadId);
        } else {
            this.doIssueNavigation(issueId, spreadId);
        }
    }

    getClickNavigation() {
        return this.clickNavigation;
    }

    setClickNavigation(bool) {
        this.clickNavigation = bool;
    }

    setFastLoad(fastload) {
        this.fastload = fastload;
    }

    setInfo() {
        // if(!self.info){
        try {
            self.issue_id = this._issue_id;
            self.spread_id = this._spread_id;
            self.minfo = {
                spread_id: this._spread_id,
                issue_id: this._issue_id,
                client_id: self.client_id,
                issue: this.dataHelper.getCurrentIssue(),
            };
        } catch (error) {
            console.error("MAGLR ERROR: [setInfo]", error);
        }
        // }
    }

    createSpreadContainer(spread_id) {
        this.setSpreadStatus(spread_id, this.SPREADSTATUSLIST.INIT);
        this.getSpreadController(spread_id).createSpreadContainer(spread_id, this.getSpreadAssetsVersion(spread_id));
    }

    /**
     * Init the spread given will call the createSpread which will load / show the spread
     * @param spread_id
     * @param data
     */
    initSpread(spread_id, data) {
        this.setInfo();
        try {
            if (this._spreads[spread_id] && !this._spreads[spread_id].settings && data) {
                this._spreads[spread_id].settings = data;
            }

            let spreadOptions = {
                interfaceOffset: this.getInterfaceOffsetSettings(),
            };

            let offset = this.interface.storyMode && this.interface.storyMode.getOffsetSettingsBySpreadId(spread_id);
            if (offset) {
                spreadOptions.spreadOffset = offset;
                this._spreads[spread_id].offset = offset;
            }

            if (!this.interface.isInTransition()) {
                this.setSpreadStatus(spread_id, this.SPREADSTATUSLIST.WAITINGTOCREATE);
                if (
                    this.getSpreadController(spread_id).createSpread(
                        spread_id,
                        data,
                        this.dataHelper.getSpreadJsVersion(spread_id),
                        spreadOptions,
                    )
                ) {
                    this.setSpreadStatus(spread_id, this.SPREADSTATUSLIST.CREATED);
                }
            } else {
                if (this._spreads[spread_id].status < this.SPREADSTATUSLIST.CREATED) {
                    this.setSpreadStatus(spread_id, this.SPREADSTATUSLIST.WAITINGTOCREATE);
                }
            }
        } catch (e) {
            console.warn(e);
        }
    }

    /**
     * Start fetching the spreads next to the current spread
     * @param spread_id
     */
    loadNeigbours(spread_id) {
        if (spread_id === this._spread_id) {
            if (this.interface.isInStoryMode()) {
                let backgroundSpreadId = this.interface.doLoadNeighbours();
                backgroundSpreadId && this._operator.fetchSpreadSettings(backgroundSpreadId, false, true);
            } else {
                let next_spread = false,
                    prev_spread = false;

                //check if main spread navigation is activated
                if (this.dataHelper.hasMainSpreadNavigation(this._issue_id)) {
                    if (this.dataHelper.isMainSpread(this._issue_id, this._spread_id)) {
                        // only load neighbours if main spread and sub spreads are never neighbors
                        let index = this.dataHelper.getMainSpreadIndexBySpreadId(this._issue_id, this._spread_id);
                        next_spread = +this.dataHelper.getMainSpreadIdByIndex(this._issue_id, index + 1);
                        prev_spread = +this.dataHelper.getMainSpreadIdByIndex(this._issue_id, index - 1);
                    }
                } else {
                    let index = this.spreadOrder.indexOf(this._spread_id);
                    next_spread = +this.spreadOrder[index + 1];
                    prev_spread = +this.spreadOrder[index - 1];
                }

                if (prev_spread && !this.getSpreadController(prev_spread).spreads.has(prev_spread)) {
                    const prevPage = document.getElementById(`maglr-article-${prev_spread}`);
                    prevPage.classList.add("preloadedSpread");
                    prevPage.setAttribute("aria-hidden", "true");
                    this.moveSpreadToMainStage(prev_spread);

                    this._operator.fetchSpreadSettings(prev_spread, false, true);
                }

                if (next_spread && !this.getSpreadController(next_spread).spreads.has(next_spread)) {
                    const nextPage = document.getElementById(`maglr-article-${next_spread}`);
                    nextPage.classList.add("preloadedSpread");
                    nextPage.setAttribute("aria-hidden", "true");
                    this.moveSpreadToMainStage(next_spread);

                    this._operator.fetchSpreadSettings(next_spread, false, true);
                }

                this.unloadSpreads();
            }
        }
    }

    resolveSpreadToIndex(spread_id) {
        spread_id = parseInt(spread_id);
        let index = this.spreadOrder.indexOf(spread_id);
        if (index > -1) {
            this._to_spread_id = spread_id;
            this.gotoSpread(spread_id);
        }
    }

    /**
     * If we receive Page_ready we start the spread an load the neighbours after 2,5 seconds
     * Start loading the neighbours if we don't receive an other ready
     * @param spread_id
     * @return {void|boolean}
     */
    spreadReady(spread_id) {
        if (spread_id === this._spread_id) {
            if (this._spreads[spread_id].status !== this.SPREADSTATUSLIST.READYTOSTART) {
                if (this._spreads[spread_id].status === this.SPREADSTATUSLIST.CREATED) {
                    this.transitionDone(spread_id);
                }
                return false;
            }

            let offset;
            if (this.interface.storyMode) {
                offset = this.interface.storyMode.searchSpreadId(spread_id).offset;
            }

            const result = this.startSpread(offset);

            Utils.simpleDebounce(this.loadNeigbours, window.LoadTimeout || 10, this)(this._spread_id);
            return result;
        } else {
            //if storyMode but not the current spread set the status to ready to start.
            this.setSpreadStatus(spread_id, this.SPREADSTATUSLIST.READYTOSTART);
        }
        return false;
    }
    getSubNavigation(spread_id) {
        let currentIssueId = this.getCurrentIssueId();
        let currentSpreadId = this.getCurrentSpreadId();
        let main_spread_id = this.dataHelper.findMainSpreadIdOfSubSpread(currentIssueId, currentSpreadId);
        return (
            (main_spread_id === spread_id && !this.dataHelper.isMainSpread(currentIssueId, currentSpreadId)) ||
            !this.dataHelper.isMainSpread(currentIssueId, spread_id)
        );
    }

    externalSanitizeCheck(event) {
        if (event.origin === window.location.origin) {
            return true;
        }

        if (event.origin === window.data_domain) {
            return true;
        }

        //ipad
        if (window.exportType === "ipad" && event.origin === "localhost://") {
            return true;
        }

        //backup export
        if (window.exportType === "backupZip" && event.origin === "null") {
            return true;
        }

        return false;
    }

    validateExternalLink(input) {
        const parsed = new URL(input, window.location.href);

        if (parsed.protocol === "http:" || parsed.protocol === "https:") {
            return true;
        }

        if (window.exportType === "backupZip" && parsed.protocol === "file:") {
            return true;
        }

        return false;
    }

    /**
     * Messages received from the spreads and do something with the given message
     */
    messageReceivedFrontend(event) {
        if (event.data === undefined || event.data.message === undefined) {
            return;
        }
        const message = event.data;
        let from_spread_id = parseInt(message.origin_spread_id);
        let index = this.spreadOrder.indexOf(from_spread_id);
        switch (message.message) {
            case "PAGE_LOADED": // advanced-assets/src/js/Spread.js + templates/content-templates/default-assets/js/
                // this.getSpreadController(from_spread_id).showSpread(from_spread_id);
                if (index > -1) {
                    if (this.getSpreadController(from_spread_id).showSpread(from_spread_id)) {
                        this.interface.pageLoaded(index, from_spread_id);
                    }
                }
                break;
            case "PAGE_READY": // advanced-assets/src/js/Spread.js + templates/content-templates/default-assets/js/videos.js
                if (index > -1) {
                    this.getSpreadController(from_spread_id)?.spreadReady?.(from_spread_id, this.getCurrentSpreadId());

                    if (this.spreadReady(from_spread_id)) {
                        this.interface.pageReady(index, event);
                    }
                }
                break;
            case "internal_link": // advanced-assets/src/js/Links.js + templates/content-templates/default-assets/js/post_publish.js
                event.preventDefault();
                this.processInternalLinkMessage({
                    issue_id: parseInt(message.issue_id, 10),
                    spread_id: parseInt(message.spread_id, 10),
                    scene_id: message.scene_id,
                    url: message.url,
                });

                break;
            case "reference_link": {
                // advanced-assets/src/js/Links.js + templates/content-templates/default-assets/js/post_publish.js
                event.preventDefault();
                let spread_id = +this.dataHelper._resolveReferenceTag(this._issue_id, message.link);
                if (spread_id) {
                    this.interface.removeSpreadScrollListing(message.spread_id);
                    if (this.dataHelper.hasMainSpreadNavigation(this._issue_id)) {
                        var isSubNavigation = this.getSubNavigation(spread_id);
                    }
                    if (this._operator.hasUrlListener()) {
                        if (!this.gotoSpread(parseInt(spread_id))) {
                            // alert(`404 - This page ( ${spread_id} ) link is not available anymore.`);
                            // NotificationBar.showNotification(window.interface_settings.settings?.text?.error_broken_link, 'error');
                        } else {
                            if (isSubNavigation) {
                                this.setClickNavigation(true);
                            }
                        }
                    } else {
                        if (isSubNavigation) {
                            this.setClickNavigation(true);
                        }
                        this.resolveSpreadToIndex(spread_id);
                    }
                }
                break;
            }
            case "external_link": {
                // advanced-assets/src/js/Links.js + templates/content-templates/default-assets/js/post_publish.js
                if (!this.externalSanitizeCheck(event)) {
                    console.warn("EVENT ORIGIN WAS WRONG", event.origin);
                    return;
                }

                let link = document.createElement("a");
                message.target && link.setAttribute("target", message.target);

                if (message.link.indexOf("//") === -1 && message.link.indexOf("mailto:") === -1) {
                    message.link = "//" + message.link;
                }
                if (message.link.indexOf("mailto:") === 0) {
                    location.href = message.link;
                    return;
                }

                if (!this.validateExternalLink(message.link)) {
                    console.warn("INVALID EXTERNAL LINK", message.link);
                    return;
                }

                link.href = message.link;

                this.offline?.sendOfflineMessage("external_link", message.link);

                if (this._operator.hasUrlListener() || this.embedMode) {
                    //this is a quick an dirty fix for safari ios > instead of opening tabs it opens in own window
                    let is_safari =
                        navigator.userAgent.indexOf("Safari") !== -1 && navigator.userAgent.indexOf("Chrome") === -1;
                    if (
                        (navigator.userAgent.match(/(iPod|iPhone|iPad)/) && !navigator.userAgent.match("CriOS")) ||
                        is_safari
                    ) {
                        link.click();
                    } else {
                        if (message.target === "_self" || message.not_blank === true) {
                            location.href = link;
                        } else {
                            if (navigator.userAgent.match(/(Edge)/)) {
                                window.open(message.link, message.target); //new policy won't open <a> element
                            } else {
                                window.open(link, message.target);
                            }
                        }
                    }
                } else {
                    let OfflineMessage = new CustomEvent("MAGLROFFLINE", {
                        detail: {
                            url: link.href,
                            target: link.target,
                        },
                    });
                    document.dispatchEvent(OfflineMessage);
                }
                break;
            }
            case "function_link": // advanced-assets/src/js/Links.js + Controller.js
                switch (message.link) {
                    case "next_spread":
                        this.nextSpread();
                        break;
                    case "previous_spread":
                        this.previousSpread();
                        break;
                    case "back": {
                        const spread_id = NavigationController.historyBack();
                        if (spread_id) {
                            let index = this.spreadOrder.indexOf(spread_id);
                            this.gotoIndex(index);
                        }
                        break;
                    }
                    case "home":
                        if (this.interface.storyMode) {
                            this.interface.storyMode.goToHome();
                        } else {
                            this.gotoIndex(0);
                        }
                        break;
                    case "overview":
                        if (self.interface_settings?.settings?.overview_enabled) {
                            this.interface.navigateBack(null);
                        } else {
                            console.warn("overview not enabled");
                        }
                        this.offline?.sendOfflineMessage("overview");
                        break;
                    case "close_level":
                        this.interface.closeLevel();
                        break;
                    case "menu.open":
                        this.interface?.sidemenu.openMenu?.() ||
                            NotificationBar.showNotification(
                                window.interface_settings?.settings?.text?.error_menu_item_not_found,
                                "error",
                            );
                        break;
                    case "menu.contact":
                    case "menu.share":
                    case "menu.search":
                    case "menu.register":
                    case "menu.pdf":
                    case "menu.spreads":
                    case "menu.issues":
                    case "menu.sitemap":
                    case "menu.info":
                    case "menu.loggedin":
                        this.interface?.sidemenu.setActiveTab(message.link.split(".")[1])?.openMenu?.() ||
                            NotificationBar.showNotification(
                                window.interface_settings?.settings?.text?.error_menu_item_not_found,
                                "error",
                            );
                        break;
                }
                break;

            case "file_link": // templates/content-templates/default-assets/js/post_publish.js
                if (!this.externalSanitizeCheck(event)) {
                    console.warn("EVENT ORIGIN WAS WRONG", event.origin);
                    return;
                }
                if (!this.validateExternalLink(message.link)) {
                    console.warn("INVALID FILE LINK", message.link);
                    return;
                }

                window.open(message.link, message.target || "_blank");
                break;

            case "multiActionLink": {
                // advanced-assets/src/js/Spread.js
                const firstAction = message.actions.shift();
                if (firstAction?.type === "internal-link") {
                    const advancedController = this.getSpreadController(
                        firstAction.data.spread_id,
                        firstAction.data.issue_id,
                    );
                    if (firstAction.data.spread_id === this.getCurrentSpreadId()) {
                        advancedController.executeMultiActionLink(firstAction.data.spread_id, message.actions);
                    } else {
                        advancedController.setMultiActionLink(firstAction.data.spread_id, message.actions);
                        this.processInternalLinkMessage(firstAction.data);
                    }
                }
                break;
            }

            case "NEXT_PAGE": // advanced-assets/src/js/Controller.js
                this.nextSpread();
                break;
            case "PREVIOUS_PAGE": // advanced-assets/src/js/Controller.js
                this.previousSpread();
                break;

            case "USER_EVENT": {
                // advanced-assets/src/js/UserEvents.js
                const from_issue_id = parseInt(message.origin_issue_id, 10);
                message.hitType = "event";
                this.analytics.sendEventUpdate(message, from_issue_id, from_spread_id);
                break;
            }
            case "SPREADAUDIO_STOP": // advanced-assets/src/js/Spread.js
                this.IssueAudio.stopSpreadAudio();
                break;
            case "SPREADAUDIO_START": // templates/content-templates/default-assets/js/functions.js
                if (
                    Array.isArray(message.data) &&
                    message.data.length > 0 &&
                    !this.IssueAudio.checkSameSource(message.data)
                ) {
                    this.IssueAudio.playSpreadAudio(message.data);
                }
                break;
            case "INSPREADAUDIO_PLAYING": // advanced-assets/src/js/Scene.js
                if (this.IssueAudio._spread_audio_muted !== true) {
                    this.IssueAudio.muteSpreadAudio(true);
                }
                break;
            case "INSPREADAUDIO_STOPPED": // advanced-assets/src/js/Scene.js
                if (this.IssueAudio._spread_audio_muted !== true) {
                    this.IssueAudio.muteSpreadAudio(false);
                }
                break;

            case "INTERFACE_ACTION": // templates/content-templates/default-assets/js/functions.js
                $($("[data-ix=" + event.data.data + "]")[0]).trigger("click.w-ix");
                break;
            case "INTERFACE_HIDE_MENU": // templates/content-templates/default-assets/js/functions.js
                this.interface.getInterfaceInstance().hideMenu();
                break;
            case "INTERFACE_SHOW_MENU": // templates/content-templates/default-assets/js/functions.js
                this.interface.getInterfaceInstance().showMenu();
                break;
            case "GET_TOP_URL": // advanced-assets + templates + form-renderer
                event.source.postMessage(
                    {
                        message: "TOP_URL",
                        data: {
                            domain: `${window.location.protocol}//${window.location.hostname + window.location.pathname}`,
                            fullURL: `${window.location.protocol}//${window.location.hostname + window.location.pathname}`,
                            hostname: window.location.hostname,
                            protocol: window.location.protocol,
                            pathname: window.location.pathname,
                            href: window.location.href,
                        },
                    },
                    "*",
                );
                break;
            case "INTERFACE_SCROLL": // templates/content-templates/default-assets/js/app_magazines.js
                // console.warn('ScrollState Disabled (unused?)');
                break;
            case "IFRAME_EMBED_MESSAGE": // advanced-assets/src/js/Embeds.js
                this.advancedController
                    .getController(this.advancedController._versions.get(message.spread_id))
                    .then((controller) => {
                        controller.embedMessageReceived(message);
                    });
                break;
            case "COOKIE_CONSENT": // advanced-assets/src/js/Spread.js
                this.CookieConsent && this.CookieConsent.show();
                break;
            case "SCENE_STARTED": // advanced-assets/src/js/Scene.js
                if (Object.hasOwn(message, "bodyScrollLock")) {
                    this.interface.updateBodyScrollLock(message.bodyScrollLock);
                }
                break;
            case "FORM_SUBMIT_SUCCESS": // form-renderer/src/pages/Confirmation.vue
                if (this.gatedContent?.hasActiveGate?.() && this.gatedContent.checkMessageOrigin(event.source)) {
                    this.gatedContent.formSubmitted();
                } else {
                    let uuid = message.uuid;
                    if (uuid && uuid.indexOf("_") > 0) {
                        uuid = message.uuid.split("_")[1];
                    }
                    this.analytics.sendEventUpdate(
                        Object.assign(
                            {},
                            this.analytics.createUserEvent(["Form", "Submit", message.formName]),
                            uuid ? { element_id: uuid } : {},
                        ),
                    );
                }
                if (message.redirectURL) {
                    const url = new URL(message.redirectURL, location.origin);
                    if (url.protocol !== "https:") {
                        console.error("Blocked non-HTTP(S) URL");
                        return;
                    }
                    window.location.href = url.href;
                }
                break;
            case "FORM_HEIGHT": // form-renderer/src/App.vue
                this.gatedContent?.hasActiveGate?.() &&
                    this.gatedContent.checkMessageOrigin(event.source) &&
                    this.gatedContent.setHeight(message.height);
                break;
            default:
                console.warn("messageReceivedFrontend unknown message: ", message);
                return;
        }
    }

    /**
     * Used by url delegate to check if the issue is changed.
     *
     * @returns {boolean}
     * @private
     */
    startIssue(issue_id, spread_id) {
        if (this._issue_id !== issue_id) {
            let switchIssue = this._issue_id !== undefined;
            this.setCurrentIssueId(issue_id);
            this.setCurrentSpreadId(spread_id);

            this._operator.resetSpreads();

            const issue = this.dataHelper.getIssueByIssueId(this._issue_id);
            // this._user_id = issue.us_user_user_id;
            this._pj_project_project_id = issue.pj_project_project_id;
            // Check if project type matches interface mode
            const isSpecialMode = SPECIAL_MODES.includes(this._pj_project_project_id);
            const isInStoryMode = this.interface.isInStoryMode();

            if ((isSpecialMode && !isInStoryMode) || (!isSpecialMode && isInStoryMode)) {
                console.error("MAGLR ERROR: Project type and interface settings don't match", {
                    project_id: this._pj_project_project_id,
                    is_special_mode: isSpecialMode,
                    is_in_story_mode: isInStoryMode,
                });
                // location.reload() // no more reloading, this can cause endless reload loops
                return false;
            }

            this.interface.getInterfaceInstance().toggleInterface(issue.hide_interface_menu);

            if (!this.fastload) {
                if (this.getCurrentPublicationType() !== 4) {
                    if (switchIssue) {
                        // Move ALL spreads (including preloaded neighbors) out of main before
                        // _renderSpreads() injects fresh elements for the new issue. Without this,
                        // a spread that was preloaded in <main> from a previous visit to this issue
                        // would share its ID with the newly injected element in content-loader,
                        // causing moveSpreadToMainStage to find the stale element and silently skip the move.
                        this.mainStage.querySelectorAll(".maglr-article").forEach((spread) => {
                            this.backStage.appendChild(spread);
                        });
                    }

                    this._operator.fetchSpreadSettings(this._spread_id, true, false);
                    this.moveSpreadToMainStage(this._spread_id);

                    if (switchIssue) {
                        this.moveInactiveToBackStage(this._spread_id);

                        if (this._spreads[this._spread_id]?.settings && !this._spreads[this._spread_id].settings.html) {
                            // preloaded pro spread does have settings but not the html, delete so it will be fetched again when needed
                            delete this._spreads[this._spread_id];
                        }
                    }
                }

                this.interface.showViewer(switchIssue);
            } else {
                this.createSpreadOrder();
                this.fixRotateSpread();
                this.setSpreadStatus(this._spread_id, this.SPREADSTATUSLIST.INIT);
                this.initSpread(this._spread_id, this.fastload);
                this.interface.rendeSidePanelByIssueId(this._issue_id);
            }

            //create this._main_spread_ids
            this.dataHelper.createMainSpreadNavigationList(this._issue_id);

            if (issue.gated) {
                this.gatedContent = new GatedContent(issue.gated);
                this.gatedContent.on("GATE_UNLOCKED", () => {
                    delete this.gatedContent;
                });
                this.gatedContent.initGate(this._issue_id, this.dataHelper.getOrderedSpreads(this._issue_id));
            }

            this.interface.reflow();
            this.interface.setCurrentIndex(this.spreadOrder.indexOf(this._spread_id));

            return true;
        }
        return false;
    }

    /**
     * initiates spread load based on the spread_id given
     * @param spread_id
     */
    fetchSpread(spread_id) {
        spread_id = parseInt(spread_id);
        if (this._operator.checkInlinePossible(spread_id)) {
            if (self.client_id !== undefined) {
                this._operator.fetchInlineSpreadSettings(window.client_id, this.getCurrentIssueId(), spread_id);
            } else {
                this._operator.fetchInlineSpreadSettings(false, this.getCurrentIssueId(), spread_id);
            }
            // $('.iframe[data-spread-id="'+spread_id+'"]').data('src', 'line');
        } else {
            this.setSpreadStatus(spread_id, this.SPREADSTATUSLIST.INIT);
            this.initSpread(spread_id, false);
            this.interface.bindWindowResize();
        }
    }

    /**
     * Set the url is called when its time to load the spreads contents
     * immediately is used when we want to load neighbours synchronously
     *
     * @param spread_id
     * @param immediately
     */
    fetchSpreadProxy(spread_id, immediately) {
        if (immediately) {
            this.fetchSpread(spread_id);
        } else {
            Utils.simpleDebounce(this.fetchSpread, 250, this)(spread_id);
        }
    }

    _urlChanged() {
        if (this.lastUrl !== location.pathname) {
            this.interface.setPageNumber();
            this.interface.urlChanged();
            this.lastUrl = location.pathname;
        }
    }

    startSpread(offset) {
        if (!this._spreads[this._spread_id]) {
            this.fetchSpreadProxy(this._spread_id, true);
            return false;
        }

        if (
            !this.interface.isInTransition() &&
            [this.SPREADSTATUSLIST.STOPPED, this.SPREADSTATUSLIST.PAUSED, this.SPREADSTATUSLIST.READYTOSTART].includes(
                this._spreads[this._spread_id].status,
            )
        ) {
            let controller = this.getSpreadController(this._spread_id);
            if (controller.spreads.has(this._spread_id)) {
                this.interface.getInterfaceInstance().setTemplateMode(this.getSpreadType(this._spread_id) === 1, true);
                controller.startSpread(this._spread_id, offset);

                this.setSpreadStatus(this._spread_id, this.SPREADSTATUSLIST.STARTED);
                window.dispatchEvent(Utils.createEvent("SPREAD_STARTED", { detail: { spreadId: this._spread_id } }));
                Utils.simpleDebounce(this.loadNeigbours, window.LoadTimeout || 10, this)(this._spread_id);

                if (window?._first.spread_id === this._spread_id) {
                    let preloadPlaceholder = document.getElementById("preloadPlaceholder");
                    preloadPlaceholder && preloadPlaceholder.parentNode.removeChild(preloadPlaceholder);
                }
                this.sendPageHit();
                this.checkGatedContent();
                return true;
            } else {
                this.initSpread(this._spread_id, this._spreads[this._spread_id].settings);
            }
        }
        return false;
    }

    pauseSpread(spread_id) {
        let controller = this.getSpreadController(spread_id);
        if (controller.spreads.has(spread_id)) {
            controller.pauseSpread(spread_id);
            this.setSpreadStatus(spread_id, this.SPREADSTATUSLIST.PAUSED);
        }
    }

    /**
     * Try firing m-spreadReady event
     * And dispatch a push in datalayer if possible
     */
    fireEventGTM() {
        try {
            let eventName = "m-spreadReady";
            let event = new Event(eventName);
            document.dispatchEvent(event);
            // event = new Event('m-spreadReady');
            if (self.dataLayer && Object.hasOwn(self.dataLayer, "push")) {
                self.dataLayer.push({ event: "m-spreadReady" });
            }
        } catch (error) {
            console.error("MAGLR ERROR: [fireEventGTM]", error);
        }
    }
    /**
     * @returns {*|jQuery|HTMLElement}
     */
    getCurrentWindowSpread() {
        return $("#" + this._spread_id);
    }

    doIssueNavigation(issue_id, spread_id, fallbackUrl = false) {
        try {
            const { issue, spread } = this.dataHelper.getIssueAndSpreadById(issue_id, spread_id);
            if (issue && spread) {
                // Check if issue or current project is in special mode (Story/Verticalswipe)
                if (
                    SPECIAL_MODES.includes(issue.pj_project_project_id) ||
                    SPECIAL_MODES.includes(this._pj_project_project_id)
                ) {
                    const safeSpreadUrl = getSafeRedirectUrl(spread.url);
                    if (safeSpreadUrl) {
                        window.location.href = safeSpreadUrl;
                    }
                } else {
                    if (!this.dataHelper.getUrlList()[spread.url]) {
                        this.dataHelper.createUrlList(spread);
                    }
                    NavigationController.pushState(
                        {},
                        spread["spread_title"],
                        `/${spread.url || `/${NavigationController.getPathname()[0]}/${issue_id}/${spread["spread_id"]}/${Utils.urlEncodeString(spread["spread_title"])}.html`}`,
                    ); //replace the %20 with a underscore
                }
            } else {
                if (window.previewMode) {
                    NotificationBar.showNotification(
                        window.interface_settings?.settings?.text?.error_internallink_preview,
                        "error",
                    );
                } else if (fallbackUrl) {
                    const safeFallback = getSafeRedirectUrl(fallbackUrl);
                    if (safeFallback) {
                        window.location.href = safeFallback;
                    }
                }
            }
        } catch (error) {
            console.error("MAGLR ERROR: issue/spread not found", error);
        }
    }

    setSpreadId(issue_id, spread_id, first_time = false) {
        const request = NavigationController.getPathname();
        const url_abbr = request[0];
        let url_issue_id = parseInt(request[1], 10);
        if (!Utils.isEmpty(issue_id)) {
            url_issue_id = parseInt(issue_id, 10);
        }
        if (Utils.isEmpty(spread_id)) {
            spread_id = parseInt(request[2], 10);
        }

        const spread = this.dataHelper.getSpreadById(url_issue_id, spread_id);
        if (!Utils.isEmpty(spread)) {
            this.setCurrentIssueId(url_issue_id);
            this.setCurrentSpreadId(spread_id);
            const spread_name = Utils.urlEncodeString(spread["spread_title"]); //replace the %20 with a underscore

            let url = spread.url || `${url_abbr}/${url_issue_id}/${spread.spread_id}/${spread_name}.html`;

            if (first_time && !window.replaceables) {
                url += location.search;
            }

            //spread_title is shown on google analytics.
            // On the initial load the browser already created a history entry for this url,
            // so replace it instead of pushing a duplicate the user has to press back through.
            if (first_time) {
                NavigationController.replaceState({}, spread["spread_title"], `/${url}`);
            } else {
                NavigationController.pushState({}, spread["spread_title"], `/${url}`);
            }
        } else {
            if (this.dataHelper.isSkippedSpread(spread_id)) {
                $(".current-spread .spreadloading")
                    .first()
                    .text("THIS SPREAD IS SKIPPED (" + spread_id + ")");
            }
            if (self._is_preview == true) {
                NavigationController.pushState({}, "preview", location.pathname + "?t"); //spread_title is shown on google analytics
                $(".current-spread .spreadloading")
                    .first()
                    .text("YOU ARE PREVIEWING WITH REALTIME DATA SPREAD IS INACTIVE -> (" + spread_id + ")");
            }
            console.error("MAGLR ERROR: spread not found skipped", spread_id);
        }
    }

    setCurrentIssueId(issue_id) {
        if (this._issue_id != issue_id) {
            this._issue_id = parseInt(issue_id, 10);
        }
    }

    setCurrentSpreadId(spread_id) {
        if (this._spread_id != spread_id) {
            this._previousSpreadId = this._spread_id;
            this._spread_id = parseInt(spread_id);
            Message.sendMessage({
                message: "SPREAD_NAVIGATION",
                spread_id: this.getCurrentSpreadId(),
                issue_id: this.getCurrentIssueId(),
            });
            if (!this._operator.hasUrlListener() || Utils.hasHashBinding()) {
                this.interface.setPageNumber();
                Utils.setLocationHash();
            }
        }
    }

    /**
     * Determine which index we will navigate to so we van call gotoSpread
     * @param index
     * @param direction
     */
    spreadNavigation(index, direction) {
        let to_spread_id, to_index;

        let isSubNavigation = !this.dataHelper.isMainSpread(this.getCurrentIssueId(), this.getCurrentSpreadId());

        if (isSubNavigation) {
            index = this.dataHelper.getMainSpreadIndexBySpreadId(this.getCurrentIssueId(), this.getCurrentSpreadId());
            if (index === -1) {
                return false;
            }
        }

        switch (direction) {
            case "next":
                to_index = index + 1;
                break;
            case "prev":
                to_index = index - 1;
                break;
        }

        if (isSubNavigation) {
            to_spread_id = this.dataHelper.getMainSpreadIdByIndex(this.getCurrentIssueId(), to_index);
        } else {
            to_spread_id = this.spreadOrder[to_index];
        }

        if (Utils.isEmpty(to_spread_id)) {
            return false;
        }

        if (!this.dataHelper.isMainSpread(this.getCurrentIssueId(), to_spread_id)) {
            return this.spreadNavigation(to_index, direction);
        }

        return this.gotoSpread(to_spread_id);
    }

    /**
     * Goto next spread based on the index
     */
    nextSpread(loop = false) {
        if (this.interface.storyMode) {
            if (this.interface.storyMode.getCarouselMode()) {
                this.interface.storyMode.carouselNext();
            } else {
                this.gotoSpread(this.interface.storyMode.getSiblingId(this.getCurrentSpreadId(), 1));
            }
        } else {
            let index = this.spreadOrder.indexOf(this.getCurrentSpreadId());

            if (index < this.spreadOrder.length - 1) {
                this.spreadNavigation(index, "next");
            } else if (loop) {
                this.gotoIndex(0);
            }
        }
    }

    /**
     * Goto previous spread based on the index
     */
    previousSpread() {
        if (this.interface.storyMode) {
            if (this.interface.storyMode.getCarouselMode()) {
                this.interface.storyMode.carouselPrevious();
            } else {
                this.gotoSpread(this.interface.storyMode.getSiblingId(this.getCurrentSpreadId(), -1));
            }
        } else {
            let index = this.spreadOrder.indexOf(this.getCurrentSpreadId());

            if (index > 0) {
                this.spreadNavigation(index, "prev");
            }
        }
    }

    /**
     * Set the current language that is used in most uri's.
     * @method setLanguage
     * @param {String} lang
     */
    setLanguage(lang) {
        this._LANG = lang;
    }

    getLanguage() {
        return this._LANG;
    }

    getLanguageId() {
        return Object.values(window.client_languages)?.find((lang) => lang.abbreviation === this._LANG)?.language_id;
    }

    moveSpreadToMainStage(spread_id) {
        let newPage = document.getElementById(`maglr-article-${spread_id}`);
        if (!newPage) {
            return null;
        }
        if (newPage.parentNode.id === "content-loader") {
            this.mainStage.appendChild(newPage);
        }
        return newPage;
    }

    moveInactiveToBackStage() {
        let inactiveSpreads = this.mainStage.querySelectorAll(`.maglr-article:not(#maglr-article-${this._spread_id})`);

        inactiveSpreads &&
            inactiveSpreads.forEach((spread) => {
                if (!spread.classList.contains("preloadedSpread")) {
                    this.backStage.appendChild(spread);
                }
            });
        return inactiveSpreads;
    }

    moveSpreadToBackStage(spread_id) {
        let spread = document.getElementById(`maglr-article-${spread_id}`);
        spread && this.backStage.appendChild(spread);
        return spread;
    }

    clearStages() {
        this.resetIssueSpreads();

        this.mainStage.querySelectorAll(`.maglr-article`).forEach((spread) => {
            this.mainStage.removeChild(spread);
        });
        this.backStage.querySelectorAll(`.maglr-article`).forEach((spread) => {
            this.backStage.removeChild(spread);
        });
        delete this._issue_id;
        delete this._spread_id;
    }

    getInterfaceOffsetSettings() {
        if (!this._interfaceOffset) {
            this._interfaceOffset = null;
            try {
                const offset = document.querySelector("[data-interfaceoffset]");
                if (offset) {
                    this._interfaceOffset = JSON.parse(offset.getAttribute("data-interfaceoffset"));
                }
            } catch (error) {
                console.error("MAGLR ERROR: [getInterfaceOffsetSettings]", error);
            }
        }

        let offset = this._interfaceOffset;
        //if the current view is mobile and the offset has mobile settings return those,
        if (this.mobileView && Object.hasOwn(this._interfaceOffset, "mobile")) {
            offset = this._interfaceOffset.mobile;
        }

        const issue = this.dataHelper.getIssueByIssueId(this._issue_id);
        if (issue.hide_interface_menu) {
            offset.height = 0;
            offset.width = 0;
        }

        return offset;
    }

    transitionDone(spread_id) {
        if (!this._spreads[spread_id]) {
            if (spread_id === this._spread_id) {
                // no settings yet (navigation outran the fetch); startSpread triggers the fetch
                this.startSpread();
            }
            return;
        }

        if (this._spreads[spread_id].status === this.SPREADSTATUSLIST.WAITINGTOCREATE) {
            this.initSpread(spread_id, this._spreads[spread_id].settings);
            document.getElementById(`${spread_id}`).classList.add("fadeOutAfterLoad");
        } else {
            if (!this.interface.isInTransition() && this._spreads[spread_id].status === this.SPREADSTATUSLIST.CREATED) {
                this.setSpreadStatus(spread_id, this.SPREADSTATUSLIST.READYTOSTART);
                document.getElementById(`${spread_id}`).classList.remove("fadeOutAfterLoad");
            }

            this.startSpread(this._spreads[spread_id].offset);
        }
    }

    setSpreadStatus(spread_id, status) {
        if (!this._spreads[spread_id]) {
            if (status === this.SPREADSTATUSLIST.INIT) {
                this._spreads[spread_id] = {};
            } else {
                return;
            }
        }
        if (
            this._spreads[spread_id].status >= this.SPREADSTATUSLIST.CREATED &&
            status < this.SPREADSTATUSLIST.CREATED
        ) {
            return;
        }
        this._spreads[spread_id].status = status;

        if (status === this.SPREADSTATUSLIST.CREATED) {
            //only send maglrEvent once per group
            this.analytics.sendGTMEventUpdate({ hitType: "event", maglrPageEvent: true }, this._issue_id, spread_id);
        }
    }

    getSpreadStatus(spread_id) {
        switch (this._spreads[spread_id]?.status) {
            case this.SPREADSTATUSLIST.INIT:
            case this.SPREADSTATUSLIST.WAITINGTOCREATE:
            case this.SPREADSTATUSLIST.CREATED:
            case this.SPREADSTATUSLIST.READYTOSTART:
                return "created";
            case this.SPREADSTATUSLIST.STOPPED:
                return "stopped";
            case this.SPREADSTATUSLIST.PAUSED:
                return "paused";
            case this.SPREADSTATUSLIST.STARTED:
                return "started";
            default:
                console.error("MAGLR ERROR: SPREAD STATUS NOT FOUND:", spread_id, this._spreads[spread_id]);
                // return `spread-not-found[${spread_id}][${this._spreads[spread_id]?.status}]`;
                break;
        }
    }

    bindKeyDownEvents() {
        $(document).on("keydown.maglr", (e) => {
            this._keyDown(e);
        });
    }

    _keyDown(e) {
        //only do something when not in input.
        const tagName = e.target.tagName.toLocaleLowerCase();
        if (document.body.dataset.sidemenu !== "open" && tagName !== "input" && tagName !== "textarea" && !e.altKey) {
            switch (e.which) {
                case 37: // arrow left
                    this.previousSpread();
                    e.preventDefault(); // prevent the default action (scroll / move caret)
                    break;
                case 39: // arrow right
                    if (this.interface.storyMode) {
                        if (this.interface.storyMode.getCarouselMode()) {
                            this.interface.storyMode.carouselNext();
                            e.preventDefault(); // prevent the default action (scroll / move caret)
                        }
                    } else {
                        this.nextSpread();
                        e.preventDefault(); // prevent the default action (scroll / move caret)
                    }
                    break;
            }
        }
    }

    checkGatedContent() {
        const spreadId =
            (this.dataHelper.hasMainSpreadNavigation(this._issue_id) &&
                this.dataHelper.findMainSpreadIdOfSubSpread(this._issue_id, this._spread_id)) ||
            this._spread_id;
        this.gatedContent && this.gatedContent.checkGate(spreadId);
    }

    getCurrentPublicationType(issue_id) {
        if (!issue_id) {
            issue_id = this._issue_id;
        }
        return (
            (issue_id && this.dataHelper.getIssueByIssueId(issue_id)?.cl_publication_type_publication_type_id) ||
            window.publication_type
        );
    }

    sendPageHit() {
        this.analytics.sendEventUpdate(
            {
                hitType: "page_view",
                page: location.pathname,
            },
            this._issue_id,
            this._spread_id,
        );
    }

    processInternalLinkMessage({ spread_id, issue_id, scene_id, url }) {
        this.interface.removeSpreadScrollListing(spread_id);
        const isSubNavigation =
            this.dataHelper.hasMainSpreadNavigation(this._issue_id) && this.getSubNavigation(spread_id);

        if (!Utils.isEmpty(scene_id)) {
            this.getSpreadController(spread_id, issue_id)._scene_id = scene_id;
        }
        if (this._operator.hasUrlListener()) {
            //check if we are in the issue we want
            if (parseInt(this._issue_id, 10) === issue_id) {
                // adding switch of issues.
                if (!this.gotoSpread(spread_id)) {
                    // alert(`404 - This page ( ${message.spread_id} ) link is not available anymore.`);
                    // NotificationBar.showNotification(window.interface_settings.settings?.text?.error_broken_link, 'error');
                } else {
                    if (this.dataHelper.hasMainSpreadNavigation(this._issue_id) && isSubNavigation) {
                        this.setClickNavigation(true);
                    }
                }
            } else {
                this.doIssueNavigation(issue_id, spread_id, url);
            }
        } else {
            if (this._issue_id !== issue_id) {
                this.offline?.sendOfflineMessage("internal_link", { issue_id, spread_id });
            }
            if (isSubNavigation) {
                this.setClickNavigation(true);
            }
            this.resolveSpreadToIndex(spread_id);
        }
    }

    setDocumentTitle() {
        if (this._spread_id && this._issue_id) {
            // spreadMeta
            const spread = this.dataHelper.getCurrentSpread();
            if (spread.js_key_value) {
                const spreadMetaTitleKeyValue = spread.js_key_value.find(
                    ({ js_key_key_id }) => js_key_key_id === SPREAD_META_TITLE_KEY,
                );
                if (spreadMetaTitleKeyValue) {
                    document.title = spreadMetaTitleKeyValue.value;
                    return;
                }
            }

            // issueMeta
            const issue = this.dataHelper.getCurrentIssue();
            if (issue.js_key_value) {
                const issueMetaTitleKeyValue = issue.js_key_value.find(
                    ({ js_key_key_id }) => js_key_key_id === ISSUE_META_TITLE_KEY,
                );
                if (issueMetaTitleKeyValue) {
                    // spreadTitle + issueMeta
                    document.title = `${spread.spread_title} - ${issueMetaTitleKeyValue.value}`;
                    return;
                }
            }

            // No metadata for both issue and spread so: spreadTitle + issueTitle
            document.title = `${spread.spread_title} - ${issue.issue_title}`;
        } else {
            document.title = window.interface_settings?.settings?.text?.magazine_environment_name || document.title;
        }
    }
}

window.frontend = new FrontendController();
window.frontend.start();
